import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Cookies from "js-cookie";
import {
    Box, Typography, Card, CardContent, Button, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Tooltip, MenuItem, InputAdornment, List, ListItemButton,
    ListItemAvatar, ListItemText, Divider, Collapse, ClickAwayListener,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    FormControl, InputLabel, Select, Pagination, TableSortLabel, Chip,
} from "@mui/material";
import {
    AddCircleOutline, DeleteOutline, VisibilityOutlined, ContactPageOutlined,
    AddRounded, Search, Close, LayersOutlined, Refresh,
    TrendingUp, TrendingDown, MonetizationOnOutlined,
} from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMAvatar from "../common/CRMAvatar";
import CRMBadge from "../common/CRMBadge";
import { useNavigate } from "react-router-dom";
import { isCrmAdmin } from "../common/crmRoles";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import {
    getSid, sortStagesByOrdering, getDefaultStageOrdering, parseStageOrdering,
} from "./pipelineStageUtils";
import { resolveContactTags } from "../common/crmContactTags";

// ─── helpers ─────────────────────────────────────────────────────────────────
const STAGE_COLOR_PALETTE = [
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#fef9c3", text: "#854d0e" },
    { bg: "#ffe4e6", text: "#be123c" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#ecfdf5", text: "#047857" },
];

const TAG_COLOR_PALETTE = [
    { bg: "#fef3c7", text: "#92400e" },
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#ffe4e6", text: "#be123c" },
];

const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
};

const fmtValue = (val) => {
    if (!val && val !== 0) return "—";
    const n = parseFloat(val);
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000)   return `₹${(n / 1000).toFixed(1)}k`;
    return `₹${n}`;
};

const normaliseArray = (raw, ...keys) => {
    if (Array.isArray(raw)) return raw;
    for (const k of keys) if (Array.isArray(raw?.[k])) return raw[k];
    return [];
};

const getContactId = (c) => c?.id ?? c?.contact_id;

const normalizeStageName = (name) => (name || "").trim().toLowerCase();

// ─── Create-Stage dialog ──────────────────────────────────────────────────────
const CreateStageDialog = ({ open, onClose, onCreated, defaultOrdering }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [name, setName]     = useState("");
    const [ordering, setOrdering] = useState("0");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName("");
            setOrdering(String(defaultOrdering ?? 0));
        }
    }, [open, defaultOrdering]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        const orderNum = parseStageOrdering(ordering);
        if (ordering.trim() !== "" && orderNum === null) {
            notifyRef.current("Display order must be 0 or greater", "error");
            return;
        }
        setSaving(true);
        try {
            const body = { stage_name: name.trim() };
            if (orderNum !== null) body.ordering = orderNum;
            const res = await axiosInstance.post(`${base_url}/crm/pipeline/stages`, body);
            notifyRef.current("Stage created successfully", "success");
            onCreated(res.data?.stage || res.data?.data || res.data);
            setName("");
            onClose();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to create stage", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                Create Pipeline Stage
            </DialogTitle>
            <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                    autoFocus
                    fullWidth
                    label="Stage name"
                    placeholder="e.g. Proposal, Negotiation..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                    fullWidth
                    label="Display order"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={ordering}
                    onChange={(e) => setOrdering(e.target.value)}
                    size="small"
                    helperText="Lower numbers appear first in the pipeline"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button
                    variant="contained"
                    size="small"
                    disabled={!name.trim() || saving}
                    onClick={handleSubmit}
                    sx={WB_SX.primaryButton}
                >
                    {saving ? "Creating…" : "Create Stage"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Edit-Stage dialog ────────────────────────────────────────────────────────
const EditStageDialog = ({ open, stage, onClose, onUpdated }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [name, setName]     = useState("");
    const [ordering, setOrdering] = useState("0");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (stage) {
            setName(stage.stage_name || "");
            setOrdering(stage.ordering != null ? String(stage.ordering) : "0");
        }
    }, [stage]);

    const handleSubmit = async () => {
        if (!name.trim() || getSid(stage) == null) return;
        const orderNum = parseStageOrdering(ordering);
        if (ordering.trim() !== "" && orderNum === null) {
            notifyRef.current("Display order must be 0 or greater", "error");
            return;
        }
        const payload = {};
        if (name.trim() !== (stage?.stage_name || "")) payload.stage_name = name.trim();
        if (orderNum !== null && orderNum !== stage?.ordering) payload.ordering = orderNum;
        if (!Object.keys(payload).length) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.put(`${base_url}/crm/pipeline/stages/${getSid(stage)}`, payload);
            notifyRef.current("Stage updated", "success");
            onUpdated();
            onClose();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to update stage", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Edit Stage</DialogTitle>
            <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                    autoFocus
                    fullWidth
                    label="Stage name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                    fullWidth
                    label="Display order"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={ordering}
                    onChange={(e) => setOrdering(e.target.value)}
                    size="small"
                    helperText="Lower numbers appear first in the pipeline"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button
                    variant="contained"
                    size="small"
                    disabled={!name.trim() || saving}
                    onClick={handleSubmit}
                    sx={WB_SX.primaryButton}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Create-Deal dialog ───────────────────────────────────────────────────────
const CreateDealDialog = ({ open, stages, defaultStageId, onClose, onCreated }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [form, setForm] = useState({
        contact_id: "",
        pipeline_stage_id: (defaultStageId != null && defaultStageId !== "") ? String(defaultStageId) : "",
        title: "",
        description: "",
        value: "",
        expected_close_date: "",
    });
    const [contactSearch, setContactSearch]   = useState("");
    const [contactResults, setContactResults] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [searching, setSearching]           = useState(false);
    const [saving, setSaving]                 = useState(false);
    const searchTimer                         = useRef(null);

    // Reset when dialog opens
    useEffect(() => {
        if (open) {
            const sid = defaultStageId != null && defaultStageId !== "" ? String(defaultStageId) : "";
            setForm({ contact_id: "", pipeline_stage_id: sid, title: "", description: "", value: "", expected_close_date: "" });
            setContactSearch("");
            setContactResults([]);
            setSelectedContact(null);
        }
    }, [open, defaultStageId]);

    const searchContacts = async (q) => {
        if (!q.trim()) { setContactResults([]); return; }
        setSearching(true);
        try {
            const res       = await axiosInstance.get(`${base_url}/crm/contacts`, { params: { search: q, per_page: 8 } });
            // Response: { success, data: { data: [...contacts], total, ... } }
            const paginator = res.data?.data ?? {};
            setContactResults(Array.isArray(paginator.data) ? paginator.data : []);
        } catch (_) {}
        finally { setSearching(false); }
    };

    const handleSearchInput = (e) => {
        const val = e.target.value;
        setContactSearch(val);
        if (selectedContact) { setSelectedContact(null); setForm((f) => ({ ...f, contact_id: "" })); }
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => searchContacts(val), 350);
    };

    const pickContact = (c) => {
        setSelectedContact(c);
        setContactSearch(c.name || c.phone || "");
        setContactResults([]);
        setForm((f) => ({ ...f, contact_id: getContactId(c) }));
    };

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async () => {
        if (!form.contact_id || !form.pipeline_stage_id) {
            notifyRef.current("Please select a contact and a pipeline stage", "warning");
            return;
        }
        setSaving(true);
        try {
            const currentUserId = Cookies.get("userId");
            const payload = {
                contact_id:          form.contact_id,
                pipeline_stage_id:   Number(form.pipeline_stage_id),
                curr_user_assign_id: currentUserId ? Number(currentUserId) : undefined,
            };
            if (form.title)               payload.title               = form.title;
            if (form.description?.trim()) payload.description         = form.description.trim();
            if (form.value)               payload.value               = parseFloat(form.value);
            if (form.expected_close_date) payload.expected_close_date = form.expected_close_date;

            const res = await axiosInstance.post(`${base_url}/crm/deals`, payload);
            notifyRef.current("Deal created successfully", "success");
            onCreated(res.data?.deal || res.data);
            onClose();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to create deal", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                Create New Deal
                <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.25 }}>

                {/* No-stages warning */}
                {stages.length === 0 && (
                    <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#fff7ed", border: "1px solid #fed7aa", display: "flex", gap: 1, alignItems: "center" }}>
                        <Typography variant="caption" sx={{ color: "#c2410c", fontWeight: 600 }}>
                            No pipeline stages found. Create a stage first before adding a deal.
                        </Typography>
                    </Box>
                )}

                {/* Contact search */}
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.75, display: "block" }}>
                        Contact <span style={{ color: "#e53e3e" }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Type a name or phone to search…"
                        value={contactSearch}
                        onChange={handleSearchInput}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {searching
                                        ? <CircularProgress size={14} />
                                        : <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                                    }
                                </InputAdornment>
                            ),
                            endAdornment: selectedContact ? (
                                <InputAdornment position="end">
                                    <CRMBadge label="✓ Selected" bg="#d1fae5" text="#065f46" />
                                </InputAdornment>
                            ) : contactSearch.length > 0 && !searching && contactResults.length === 0 ? (
                                <InputAdornment position="end">
                                    <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>No results</Typography>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                borderColor: selectedContact ? "#16a34a" : undefined,
                            },
                        }}
                    />

                    {/* Helper text */}
                    {!selectedContact && (
                        <Typography variant="caption" sx={{ color: "text.disabled", mt: 0.5, display: "block", pl: 0.5 }}>
                            {contactSearch.length === 0
                                ? "Start typing to search contacts, then click a result to select."
                                : contactResults.length > 0
                                ? "Click a contact below to select it."
                                : null}
                        </Typography>
                    )}

                    {/* Search results dropdown */}
                    <Collapse in={contactResults.length > 0}>
                        <ClickAwayListener onClickAway={() => setContactResults([])}>
                            <List
                                dense
                                sx={{
                                    mt: 0.5, border: `1px solid ${WB_UI.color.border}`,
                                    borderRadius: 2, overflow: "hidden",
                                    bgcolor: "background.paper",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                                }}
                            >
                                {contactResults.map((c, idx) => (
                                    <ListItemButton
                                        key={String(getContactId(c) ?? idx)}
                                        onClick={() => pickContact(c)}
                                        divider={idx < contactResults.length - 1}
                                        sx={{
                                            py: 1,
                                            "&:hover": { backgroundColor: "rgba(22,163,74,0.06)" },
                                        }}
                                    >
                                        <ListItemAvatar sx={{ minWidth: 40 }}>
                                            <CRMAvatar initials={getInitials(c.name || c.phone)} size={28} color="#1a2333" />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name || "—"}</Typography>}
                                            secondary={<Typography variant="caption" color="text.secondary">{c.phone}{c.company_name ? ` · ${c.company_name}` : ""}</Typography>}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </ClickAwayListener>
                    </Collapse>
                </Box>

                {/* Pipeline stage */}
                <TextField
                    select
                    fullWidth
                    label="Pipeline Stage *"
                    value={form.pipeline_stage_id}
                    onChange={(e) => setForm((f) => ({ ...f, pipeline_stage_id: String(e.target.value) }))}
                    size="small"
                    disabled={stages.length === 0}
                    helperText={!form.pipeline_stage_id && stages.length > 0 ? "Please select a stage" : ""}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    {stages.map((s) => (
                        <MenuItem key={String(getSid(s))} value={String(getSid(s))}>{s.stage_name}</MenuItem>
                    ))}
                </TextField>

                {/* Deal title */}
                <TextField
                    fullWidth
                    label="Deal Title"
                    placeholder="e.g. Annual subscription — TechCorp"
                    value={form.title}
                    onChange={set("title")}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                <TextField
                    fullWidth
                    label="Description (optional)"
                    placeholder="Internal notes about this deal…"
                    value={form.description}
                    onChange={set("description")}
                    size="small"
                    multiline
                    rows={2}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                {/* Value + Close date */}
                <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                        fullWidth
                        label="Deal Value (₹)"
                        type="number"
                        placeholder="e.g. 50000"
                        value={form.value}
                        onChange={set("value")}
                        size="small"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <TextField
                        fullWidth
                        label="Expected Close Date"
                        type="date"
                        value={form.expected_close_date}
                        onChange={set("expected_close_date")}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                </Box>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
                {/* Inline hint when button is still disabled */}
                {(!form.contact_id || !form.pipeline_stage_id) && (
                    <Typography variant="caption" sx={{ color: "text.disabled", flex: 1, fontSize: "0.72rem" }}>
                        {!form.contact_id && !form.pipeline_stage_id
                            ? "Select a contact and a pipeline stage to continue"
                            : !form.contact_id
                            ? "Click a contact from the search dropdown to continue"
                            : "Select a pipeline stage to continue"}
                    </Typography>
                )}
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button
                    variant="contained"
                    size="small"
                    disabled={!form.contact_id || !form.pipeline_stage_id || saving}
                    onClick={handleSubmit}
                    sx={WB_SX.primaryButton}
                >
                    {saving ? "Creating…" : "Create Deal"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Status & stage colour helpers ───────────────────────────────────────────
const STATUS_STYLE = {
    open:   { bg: "#eff6ff", text: "#1d4ed8" },
    won:    { bg: "#d1fae5", text: "#065f46" },
    lost:   { bg: "#fee2e2", text: "#b91c1c" },
};

const DEAL_STATUS_LABELS = {
    open:  "Open",
    won:   "Won",
    lost:  "Lost",
};

const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// ─── Main pipeline (table view) ───────────────────────────────────────────────
const CRMPipeline = () => {
    const navigate = useNavigate();
    const userIsCrmAdmin = useMemo(() => isCrmAdmin(), []);
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    // ── data ──
    const [stages, setStages]         = useState([]);
    const [deals, setDeals]           = useState([]);
    const [total, setTotal]           = useState(0);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);

    // ── filters & pagination ──
    const [search, setSearch]         = useState("");
    const [stageFilter, setStageFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage]             = useState(1);
    const perPage                     = 20;
    const searchTimer                 = useRef(null);

    // ── sorting ──
    const [sortField, setSortField]   = useState("created_at");
    const [sortDir, setSortDir]       = useState("desc");

    const orderedStages = useMemo(() => sortStagesByOrdering(stages), [stages]);
    const defaultStageOrdering = useMemo(() => getDefaultStageOrdering(stages), [stages]);
    const defaultStageFilterAppliedRef = useRef(false);

    // ── dialogs ──
    const [createStageOpen, setCreateStageOpen]     = useState(false);
    const [editStage, setEditStage]                 = useState(null);
    const [deleteStageTarget, setDeleteStageTarget] = useState(null);
    const [deletingStage, setDeletingStage]         = useState(false);
    const [deleteDealTarget, setDeleteDealTarget]   = useState(null); // deal object
    const [deletingDeal, setDeletingDeal]           = useState(false);
    const [dealDialogStageId, setDealDialogStageId] = useState(null);

    // ── fetch stages (for filter dropdown) ───────────────────────────────────
    const fetchStages = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`${base_url}/crm/pipeline/stages`);
            const raw = res.data;
            const list = Array.isArray(raw?.data)
                ? raw.data
                : Array.isArray(raw?.data?.data)
                ? raw.data.data
                : normaliseArray(raw, "stages");
            setStages(list);
        } catch (_) {}
    }, []);

    // ── fetch deals (paginated, filtered, sorted) ─────────────────────────────
    const fetchDeals = useCallback(async (pg = 1, sq = search, sf = stageFilter, st = statusFilter) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: pg, per_page: perPage };
            if (sq) params.search  = sq;
            if (sf) params.stage   = sf;
            if (st) params.status  = st;

            const res       = await axiosInstance.get(`${base_url}/crm/deals`, { params });
            const paginator = res.data?.data ?? {};
            const list      = Array.isArray(paginator.data)
                ? paginator.data
                : normaliseArray(res.data, "data");
            setDeals(list);
            setTotal(paginator.total ?? list.length);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load deals";
            setError(msg);
            notifyRef.current(msg, "error");
        } finally {
            setLoading(false);
        }
    }, [search, stageFilter, statusFilter, perPage]);

    useEffect(() => {
        fetchStages();
    }, [fetchStages]);

    // Default pipeline view: deals in "Proposed" (once stages are loaded).
    useEffect(() => {
        if (!stages.length || defaultStageFilterAppliedRef.current) return;
        defaultStageFilterAppliedRef.current = true;
        const proposed = stages.find((s) => normalizeStageName(s.stage_name) === "proposed");
        if (proposed) setStageFilter(String(getSid(proposed)));
    }, [stages]);

    useEffect(() => {
        fetchDeals(page, search, stageFilter, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, stageFilter, statusFilter]);

    // ── handlers ─────────────────────────────────────────────────────────────
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => { setPage(1); fetchDeals(1, val, stageFilter, statusFilter); }, 400);
    };

    const handleSort = (field) => {
        const dir = sortField === field && sortDir === "asc" ? "desc" : "asc";
        setSortField(field);
        setSortDir(dir);
    };

    const confirmDeleteDeal = async () => {
        if (!deleteDealTarget) return;
        setDeletingDeal(true);
        try {
            await axiosInstance.delete(`${base_url}/crm/deals/${deleteDealTarget.id}`);
            notifyRef.current("Deal deleted successfully", "success");
            setDeleteDealTarget(null);
            fetchDeals(page, search, stageFilter, statusFilter);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to delete deal", "error");
        } finally {
            setDeletingDeal(false);
        }
    };

    const handleStageCreated = () => {
        fetchStages();
    };

    const handleStageUpdated = () => {
        fetchStages();
        fetchDeals(page, search, stageFilter, statusFilter);
    };

    const confirmDeleteStage = async () => {
        if (!deleteStageTarget) return;
        setDeletingStage(true);
        try {
            await axiosInstance.delete(`${base_url}/crm/pipeline/stages/${getSid(deleteStageTarget)}`);
            notifyRef.current("Stage deleted", "success");
            setStages((prev) => prev.filter((s) => getSid(s) !== getSid(deleteStageTarget)));
            setDeleteStageTarget(null);
            fetchDeals(page, search, stageFilter, statusFilter);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Cannot delete stage — it may have active deals", "error");
        } finally {
            setDeletingStage(false);
        }
    };

    // ── client-side sort (on current page) ────────────────────────────────────
    const sorted = [...deals].sort((a, b) => {
        let va, vb;
        if (sortField === "expected_close_date") {
            va = a.expected_close_date ? new Date(a.expected_close_date) : 0;
            vb = b.expected_close_date ? new Date(b.expected_close_date) : 0;
        } else if (sortField === "created_at") {
            va = new Date(a.created_at || 0); vb = new Date(b.created_at || 0);
        } else {
            va = (a[sortField] || "").toString().toLowerCase();
            vb = (b[sortField] || "").toString().toLowerCase();
        }
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
    });

    const totalValue = deals.reduce((s, d) => s + parseFloat(d.value || 0), 0);
    const wonDeals   = deals.filter((d) => d.status === "won").length;
    const totalPages = Math.ceil(total / perPage);

    const openDealInNewTab = (dealId) => {
        window.open(`/crm/pipeline/${dealId}`, "_blank", "noopener,noreferrer");
    };

    const SortCell = ({ field, label, align = "left" }) => (
        <TableCell align={align} sx={{ ...WB_SX.tableHeadCell, cursor: "pointer", userSelect: "none" }}>
            <TableSortLabel
                active={sortField === field}
                direction={sortField === field ? sortDir : "asc"}
                onClick={() => handleSort(field)}
                sx={{ "& .MuiTableSortLabel-icon": { fontSize: 14 } }}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <Box sx={WB_SX.page}>
            {/* ── Page header ────────────────────────────────────────────── */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={WB_SX.pageTitle}>Deal Pipeline</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} deal{total !== 1 ? "s" : ""} · {fmtValue(totalValue)} pipeline value
                        {wonDeals > 0 && ` · ${wonDeals} won`}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {userIsCrmAdmin && (
                    <Button
                        variant="outlined"
                        startIcon={<LayersOutlined fontSize="small" />}
                        size="small"
                        onClick={() => navigate("/crm/stages")}
                        sx={{ ...WB_SX.secondaryButton, fontSize: "0.82rem" }}
                    >
                        Manage Stages
                    </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutline fontSize="small" />}
                        size="small"
                        onClick={() => setDealDialogStageId(getSid(orderedStages[0]) || "")}
                        disabled={stages.length === 0}
                        sx={WB_SX.primaryButton}
                    >
                        New Deal
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchDeals(page, search, stageFilter, statusFilter)} sx={WB_SX.secondaryButton}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── Filters ────────────────────────────────────────────────── */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
                <TextField
                    size="small"
                    placeholder="Search deal title, contact..."
                    value={search}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 250, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Stage</InputLabel>
                    <Select
                        value={stageFilter}
                        label="Stage"
                        onChange={(e) => { setStageFilter(e.target.value); setPage(1); }}
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">All stages</MenuItem>
                        {orderedStages.map((s) => (
                            <MenuItem key={getSid(s)} value={getSid(s)}>{s.stage_name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="won">Won</MenuItem>
                        <MenuItem value="lost">Lost</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ── Stage chips (quick filter) ─────────────────────────────── */}
            {stages.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {orderedStages.map((s, si) => {
                        const c       = STAGE_COLOR_PALETTE[si % STAGE_COLOR_PALETTE.length];
                        const sid     = getSid(s);
                        const active  = stageFilter === sid;
                        return (
                            <Chip
                                key={sid}
                                label={s.stage_name}
                                size="small"
                                onClick={() => { setStageFilter(active ? "" : sid); setPage(1); }}
                                sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    backgroundColor: active ? c.text : c.bg,
                                    color: active ? "#fff" : c.text,
                                    border: `1px solid ${c.text}22`,
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                    "&:hover": { backgroundColor: c.text, color: "#fff" },
                                }}
                            />
                        );
                    })}
                    {stageFilter && (
                        <Chip
                            label="Clear filter"
                            size="small"
                            onDelete={() => { setStageFilter(""); setPage(1); }}
                            sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                        />
                    )}
                </Box>
            )}

            {/* ── Error ──────────────────────────────────────────────────── */}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* ── Table ──────────────────────────────────────────────────── */}
            <Card elevation={0} sx={WB_SX.card}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={WB_SX.tableHeadCell}>Contact</TableCell>
                                <SortCell field="title"               label="Deal name" />
                                <TableCell sx={WB_SX.tableHeadCell}>Stage</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Status</TableCell>
                                <SortCell field="expected_close_date" label="Close Date" />
                                <TableCell sx={WB_SX.tableHeadCell}>Assigned To</TableCell>
                                <SortCell field="created_at"          label="Created" />
                                <TableCell sx={WB_SX.tableHeadCell} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : sorted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                                            <MonetizationOnOutlined sx={{ fontSize: 40, color: "text.disabled" }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {stages.length === 0 ? "Create a pipeline stage first, then add deals." : "No deals found. Try adjusting your filters."}
                                            </Typography>
                                            {stages.length === 0 ? (
                                                <Button size="small" variant="contained" onClick={() => setCreateStageOpen(true)} sx={WB_SX.primaryButton}>
                                                    Create First Stage
                                                </Button>
                                            ) : (
                                                <Button size="small" variant="contained" startIcon={<AddCircleOutline fontSize="small" />} onClick={() => setDealDialogStageId(getSid(orderedStages[0]) || "")} sx={WB_SX.primaryButton}>
                                                    Add First Deal
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sorted.map((d, i) => {
                                    const contactName = d.contact?.name  || d.title || "Unknown";
                                    const contactId = d.contact_id || d.contact?.id;
                                    const contactTags = resolveContactTags(d.contact);
                                    const contactPhone = d.contact?.phone || "";
                                    const contactAltPhone = d.contact?.alternate_contact_number || "";
                                    const company     = d.contact?.company_name || "";
                                    const stageName   = d.stage?.stage_name || d.pipeline_stage?.stage_name || "";
                                    const stageIdx    = orderedStages.findIndex((s) => getSid(s) === d.pipeline_stage_id);
                                    const sc          = STAGE_COLOR_PALETTE[stageIdx >= 0 ? stageIdx % STAGE_COLOR_PALETTE.length : 0];
                                    const ss          = STATUS_STYLE[d.status] || STATUS_STYLE.open;
                                    const assignee    = d.current_assigned_user?.name || d.assigned_user?.name || "—";
                                    const closeDate   = fmtDate(d.expected_close_date);
                                    const createdAgo  = d.created_at
                                        ? (() => { const days = Math.floor((Date.now() - new Date(d.created_at)) / 86400000); return days === 0 ? "Today" : `${days}d ago`; })()
                                        : "—";

                                    return (
                                        <TableRow
                                            key={d.id}
                                            sx={{
                                                ...WB_SX.tableRow,
                                                background: i % 2 === 0 ? "#fff" : "#fafafa",
                                                cursor: "pointer",
                                                "&:hover": { background: "rgba(17,24,39,0.03) !important" },
                                            }}
                                            onClick={() => openDealInNewTab(d.id)}
                                        >
                                            {/* Contact */}
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                                                    <CRMAvatar initials={getInitials(contactName)} size={30} color="#1a2333" />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{contactName}</Typography>
                                                        {contactPhone && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                {contactPhone}
                                                            </Typography>
                                                        )}
                                                        {contactAltPhone && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                Alt {contactAltPhone}
                                                            </Typography>
                                                        )}
                                                        {company && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{company}</Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* Deal name */}
                                            <TableCell sx={{ py: 1.5, maxWidth: 220 }}>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 600 }}
                                                        noWrap
                                                        title={d.title || ""}
                                                    >
                                                        {d.title?.trim() ? d.title : "—"}
                                                    </Typography>
                                                    {contactTags.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                px: 1,
                                                                py: 0.6,
                                                                borderRadius: 1.5,
                                                                border: "1px solid #e5e7eb",
                                                                backgroundColor: "#f9fafb",
                                                            }}
                                                        >
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mr: 0.25 }}>
                                                                Tags:
                                                            </Typography>
                                                            {contactTags.map((t, ti) => {
                                                                const tc = TAG_COLOR_PALETTE[ti % TAG_COLOR_PALETTE.length];
                                                                return (
                                                                    <CRMBadge key={t.id ?? ti} label={t.tag_name} bg={tc.bg} text={tc.text} />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* Stage */}
                                            <TableCell>
                                                {stageName
                                                    ? <CRMBadge label={stageName} bg={sc.bg} text={sc.text} />
                                                    : <Typography variant="caption" color="text.disabled">—</Typography>
                                                }
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell>
                                                <CRMBadge label={DEAL_STATUS_LABELS[d.status] ?? "Open"} bg={ss.bg} text={ss.text} />
                                            </TableCell>

                                            {/* Close Date */}
                                            <TableCell>
                                                <Typography variant="caption" color={closeDate === "—" ? "text.disabled" : "text.primary"}>
                                                    {closeDate}
                                                </Typography>
                                            </TableCell>

                                            {/* Assigned To */}
                                            <TableCell>
                                                <Typography variant="caption" color={assignee === "—" ? "text.disabled" : "text.primary"}>
                                                    {assignee}
                                                </Typography>
                                            </TableCell>

                                            {/* Created */}
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">{createdAgo}</Typography>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                    <Tooltip title="View deal">
                                                        <IconButton size="small" onClick={() => openDealInNewTab(d.id)} sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}>
                                                            <VisibilityOutlined sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={contactId ? "Open contact" : "No contact linked"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!contactId}
                                                                onClick={() => window.open(`/crm/contacts/${contactId}`, "_blank", "noopener,noreferrer")}
                                                                sx={{ color: "text.secondary", "&:hover": { color: "#1d4ed8" } }}
                                                            >
                                                                <ContactPageOutlined sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Deal">
                                                        <IconButton size="small" onClick={() => setDeleteDealTarget(d)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                                                            <DeleteOutline sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.75 }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total} deals
                        </Typography>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, v) => setPage(v)}
                            size="small"
                            shape="rounded"
                        />
                    </Box>
                )}
            </Card>

            {/* ── Stage management dialogs ──────────────────────────────── */}
            <CreateStageDialog
                open={createStageOpen}
                onClose={() => setCreateStageOpen(false)}
                onCreated={handleStageCreated}
                defaultOrdering={defaultStageOrdering}
            />
            <EditStageDialog
                open={!!editStage}
                stage={editStage}
                onClose={() => setEditStage(null)}
                onUpdated={handleStageUpdated}
            />
            <Dialog
                open={!!deleteStageTarget}
                onClose={() => !deletingStage && setDeleteStageTarget(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Delete Stage</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete <strong>"{deleteStageTarget?.stage_name}"</strong>?
                        This is blocked if the stage has active deals.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button size="small" onClick={() => setDeleteStageTarget(null)} sx={WB_SX.secondaryButton} disabled={deletingStage}>Cancel</Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={confirmDeleteStage}
                        disabled={deletingStage}
                        sx={{ ...WB_SX.primaryButton, backgroundColor: "#dc2626", "&:hover": { backgroundColor: "#b91c1c" } }}
                    >
                        {deletingStage ? "Deleting…" : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete deal confirmation ──────────────────────────────── */}
            <Dialog
                open={!!deleteDealTarget}
                onClose={() => !deletingDeal && setDeleteDealTarget(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(239,68,68,0.08)", display: "grid", placeItems: "center" }}>
                            <DeleteOutline sx={{ fontSize: 16, color: "error.main" }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Delete Deal</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setDeleteDealTarget(null)} disabled={deletingDeal}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2.5, pb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Are you sure you want to permanently delete the deal for{" "}
                        <strong>{deleteDealTarget?.contact?.name || deleteDealTarget?.title || "this contact"}</strong>?
                    </Typography>
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button size="small" onClick={() => setDeleteDealTarget(null)} disabled={deletingDeal} sx={WB_SX.secondaryButton}>
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={confirmDeleteDeal}
                        disabled={deletingDeal}
                        sx={{ ...WB_SX.primaryButton, backgroundColor: "error.main", "&:hover": { backgroundColor: "error.dark" } }}
                    >
                        {deletingDeal ? "Deleting…" : "Yes, Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Create deal ───────────────────────────────────────────── */}
            <CreateDealDialog
                open={dealDialogStageId !== null}
                stages={orderedStages}
                defaultStageId={dealDialogStageId}
                onClose={() => setDealDialogStageId(null)}
                onCreated={() => fetchDeals(page, search, stageFilter, statusFilter)}
            />
        </Box>
    );
};

export default CRMPipeline;
